import React, { useState } from 'react';
import { IStateRequirement, ICategory } from '../api/StateRequirements';

interface StateRequirementFormProps {
  requirement: Partial<IStateRequirement> | null;
  stateName: string;
  onSave: (data: Partial<IStateRequirement>) => void;
  onCancel: () => void;
}

// Subject-matter category tags only — delivery-method constraints live on the
// StateRequirement itself (lectureMinCredits field), never here.
const CATEGORY_TAGS_BY_PROFESSION: Record<string, string[]> = {
  'ARCH': ['HSW', 'ENERGY', 'ACCESSIBILITY', 'BARRIER_FREE', 'SUSTAINABLE_DESIGN', 'BUILDING_CODES', 'ETHICS'],
  'LA':   ['HSW', 'SUSTAINABILITY', 'SITE_DESIGN', 'ECOLOGY', 'ETHICS', 'WATER_MANAGEMENT'],
  'PE':   ['TECHNICAL', 'ETHICS', 'STRUCTURAL', 'MECHANICAL', 'ELECTRICAL', 'SOLAR', 'ENERGY', 'FLORIDA LAWS AND RULES'],
  'RE':   ['ETHICS', 'IMPLICIT BIAS AWARENESS', 'CULTURAL COMPETENCY', 'FAIR HOUSING AND/OR DISCRIMINATION', 'ETHICAL BUSINESS PRACTICES', 'RECENT LEGAL MATTERS', 'LAW OF AGENCY'],
  'LS':   ['ETHICS', 'BOUNDARY_LAW', 'TECHNICAL', 'GPS', 'PHOTOGRAMMETRY'],
  'LAW':  ['GENERAL', 'ETHICS', 'DIVERSITY', 'SKILLS', 'PRO BONO', 'NY PRACTICE', 'NY PROF', 'CT ETHICS', 'NJ ETHICS', 'NJ DIVERSITY', 'NJ SKILLS', 'NJ TRUST', 'NJ PRACTICE', 'NJ RECORDKEEPING', 'NJ COMPETENCE', 'CA LEGAL ETHICS', 'CA ELIMINATION OF BIAS', 'CA COMPETENCE'],
  'LMHC': ['CHILD_ABUSE', 'GENERAL_MHC'],
  'PHARM': ['MED_ERROR', 'COMPOUNDING', 'COLLAB_THERAPY', 'PHARMA_LAW', 'OPIOID_ED', 'IMMUNIZATION', 'GENERAL_PHARM'],
  'DEFAULT': ['HSW', 'ETHICS', 'ENERGY', 'ACCESSIBILITY', 'TECHNICAL'],
};

// Tags that represent delivery-method constraints — they must NOT be used as subject
// categories. The form shows a warning if an admin accidentally types one of these.
const DELIVERY_METHOD_TAGS = new Set([
  'LIVE_INST', 'LIVE_REQUIRED', 'LIVE_INTERACTIVE', 'LIVE_MIN', 'LIVE_LECTURE',
  'SELF_STUDY', 'SELF_STUDY_MAX', 'DIDACTIC_REQUIRED', 'DISTANCE_LEARNING',
  'LECTURE', 'INTERACTIVE',
]);

const StateRequirementForm: React.FC<StateRequirementFormProps> = ({
  requirement,
  stateName,
  onSave,
  onCancel
}) => {
  const [formData, setFormData] = useState<Partial<IStateRequirement>>({
    totalCreditsRequired: 0,
    cycleLengthYears: 2,
    allowCarryover: false,
    carryoverMaxCredits: 0,
    lectureMinCredits: undefined,
    notes: '',
    categoryIDs: [],
    hasFixedCycleDates: false,
    fixedCycleStartMonth: undefined,
    fixedCycleStartDay: undefined,
    fixedCycleEndMonth: undefined,
    fixedCycleEndDay: undefined,
    cycleDateNotes: '',
    ...requirement
  });

  // Get profession-specific category tags
  const professionID = formData.professionID || 'DEFAULT';
  const availableCategoryTags = CATEGORY_TAGS_BY_PROFESSION[professionID] || CATEGORY_TAGS_BY_PROFESSION['DEFAULT'];

  const [categories, setCategories] = useState<ICategory[]>(
    requirement?.categoryIDs || []
  );
  const [validationError, setValidationError] = useState<string>('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate no duplicate category tags
    const categoryTags = categories.map(c => c.categoryTag.toUpperCase()).filter(Boolean);
    const duplicates = categoryTags.filter((tag, index) => categoryTags.indexOf(tag) !== index);
    
    if (duplicates.length > 0) {
      const uniqueDuplicates = Array.from(new Set(duplicates));
      setValidationError(`Duplicate category tags found: ${uniqueDuplicates.join(', ')}. Each category tag must be unique.`);
      return;
    }
    
    // Clear validation error
    setValidationError('');
    
    // Include categories in the categoryIDs field
    onSave({
      ...formData,
      categoryIDs: categories
    });
  };

  const handleChange = (field: keyof IStateRequirement, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddCategory = () => {
    const newCat: ICategory = {
      _id: '',
      categoryTag: '',
      name: '',
      stateID: formData.stateID || 0,
      professionID: formData.professionID || '',
      minCredits: 0,
      maxCredits: undefined,
      description: ''
    };
    setCategories(prev => [...prev, newCat]);
  };

  const handleRemoveCategory = (index: number) => {
    setCategories(prev => prev.filter((_, i) => i !== index));
  };

  const handleCategoryChange = (index: number, field: keyof ICategory, value: any) => {
    setCategories(prev => prev.map((cat, i) => 
      i === index ? { ...cat, [field]: value } : cat
    ));
    
    // Clear validation error when user makes changes
    if (validationError) {
      setValidationError('');
    }
  };
  
  // Derived: self-study maximum = total required − lecture minimum
  const selfStudyMaxCredits =
    formData.totalCreditsRequired && formData.lectureMinCredits
      ? Math.max(0, formData.totalCreditsRequired - formData.lectureMinCredits)
      : null;

  // Warn if a category tag looks like a delivery-method constraint
  const isDeliveryMethodTag = (tag: string) =>
    DELIVERY_METHOD_TAGS.has(tag.toUpperCase());

  // Check if a category tag is duplicated
  const isDuplicateTag = (tag: string, currentIndex: number): boolean => {
    if (!tag) return false;
    const upperTag = tag.toUpperCase();
    return categories.some((cat, i) => 
      i !== currentIndex && cat.categoryTag.toUpperCase() === upperTag
    );
  };

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {requirement?._id ? 'Edit' : 'Add'} State Requirement - {stateName}
            </h5>
            <button type="button" className="btn-close" onClick={onCancel}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {validationError && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <strong>Validation Error:</strong> {validationError}
                  <button type="button" className="btn-close" onClick={() => setValidationError('')}></button>
                </div>
              )}
              
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Total Credits Required <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.totalCreditsRequired}
                    onChange={(e) => handleChange('totalCreditsRequired', parseFloat(e.target.value))}
                    min="0"
                    step="0.5"
                    required
                  />
                  <small className="text-muted">Total credits needed per cycle</small>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Cycle Length (Years) <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.cycleLengthYears}
                    onChange={(e) => handleChange('cycleLengthYears', parseInt(e.target.value))}
                    min="1"
                    max="5"
                    required
                  />
                  <small className="text-muted">Renewal cycle length (1-5 years)</small>
                </div>
              </div>

              <div className="mb-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="allowCarryover"
                    checked={formData.allowCarryover || false}
                    onChange={(e) => handleChange('allowCarryover', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="allowCarryover">
                    Allow Credit Carryover
                  </label>
                </div>
                <small className="text-muted">Can excess credits carry over to next cycle?</small>
              </div>

              {formData.allowCarryover && (
                <div className="mb-3">
                  <label className="form-label">Maximum Carryover Credits</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.carryoverMaxCredits || 0}
                    onChange={(e) => handleChange('carryoverMaxCredits', parseFloat(e.target.value))}
                    min="0"
                    step="0.5"
                  />
                  <small className="text-muted">Maximum credits that can carry over</small>
                </div>
              )}

              {/* Delivery Method Constraints */}
              <div className="mb-3">
                <hr />
                <h6 className="mb-2">Delivery Method Constraints</h6>
                <small className="text-muted d-block mb-3">
                  Many boards require a minimum number of <em>live / interactive</em> hours and cap
                  how many hours may come from self-study. Set that here — <strong>not</strong> as
                  subject-matter categories below.
                </small>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Live Instruction Minimum (hours)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.lectureMinCredits ?? ''}
                      onChange={(e) =>
                        handleChange(
                          'lectureMinCredits',
                          e.target.value === '' ? undefined : parseFloat(e.target.value)
                        )
                      }
                      min="0"
                      step="0.5"
                      placeholder="e.g. 24"
                    />
                    <small className="text-muted">
                      Minimum hours a licensee must complete as live/interactive CE.
                      Leave blank if the board has no live instruction requirement.
                    </small>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Self-Study Maximum (hours)</label>
                    <input
                      type="number"
                      className="form-control bg-light"
                      value={selfStudyMaxCredits ?? ''}
                      readOnly
                      placeholder="Computed automatically"
                    />
                    <small className="text-muted">
                      {selfStudyMaxCredits !== null
                        ? `= ${formData.totalCreditsRequired} total − ${formData.lectureMinCredits} live`
                        : 'Automatically calculated as Total Required − Live Minimum.'}
                    </small>
                  </div>
                </div>
              </div>

              {/* Fixed Cycle Dates Section */}
              <div className="mb-3">
                <hr />
                <h6 className="mb-3">Fixed Cycle Dates</h6>
                <div className="form-check mb-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="hasFixedCycleDates"
                    checked={formData.hasFixedCycleDates || false}
                    onChange={(e) => handleChange('hasFixedCycleDates', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="hasFixedCycleDates">
                    Has Fixed Cycle Dates
                  </label>
                  <small className="text-muted d-block">Enable if this state has standardized renewal periods (e.g., calendar year)</small>
                </div>

                {formData.hasFixedCycleDates && (
                  <>
                    <div className="row mb-3">
                      <div className="col-6">
                        <label className="form-label">Start Month</label>
                        <select
                          className="form-select"
                          value={formData.fixedCycleStartMonth || ''}
                          onChange={(e) => handleChange('fixedCycleStartMonth', e.target.value ? parseInt(e.target.value) : undefined)}
                        >
                          <option value="">Select month...</option>
                          <option value="1">January</option>
                          <option value="2">February</option>
                          <option value="3">March</option>
                          <option value="4">April</option>
                          <option value="5">May</option>
                          <option value="6">June</option>
                          <option value="7">July</option>
                          <option value="8">August</option>
                          <option value="9">September</option>
                          <option value="10">October</option>
                          <option value="11">November</option>
                          <option value="12">December</option>
                        </select>
                      </div>
                      <div className="col-6">
                        <label className="form-label">Start Day</label>
                        <input
                          type="number"
                          className="form-control"
                          value={formData.fixedCycleStartDay || ''}
                          onChange={(e) => handleChange('fixedCycleStartDay', e.target.value ? parseInt(e.target.value) : undefined)}
                          min="1"
                          max="31"
                          placeholder="1-31"
                        />
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-6">
                        <label className="form-label">End Month</label>
                        <select
                          className="form-select"
                          value={formData.fixedCycleEndMonth || ''}
                          onChange={(e) => handleChange('fixedCycleEndMonth', e.target.value ? parseInt(e.target.value) : undefined)}
                        >
                          <option value="">Select month...</option>
                          <option value="1">January</option>
                          <option value="2">February</option>
                          <option value="3">March</option>
                          <option value="4">April</option>
                          <option value="5">May</option>
                          <option value="6">June</option>
                          <option value="7">July</option>
                          <option value="8">August</option>
                          <option value="9">September</option>
                          <option value="10">October</option>
                          <option value="11">November</option>
                          <option value="12">December</option>
                        </select>
                      </div>
                      <div className="col-6">
                        <label className="form-label">End Day</label>
                        <input
                          type="number"
                          className="form-control"
                          value={formData.fixedCycleEndDay || ''}
                          onChange={(e) => handleChange('fixedCycleEndDay', e.target.value ? parseInt(e.target.value) : undefined)}
                          min="1"
                          max="31"
                          placeholder="1-31"
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Cycle Date Notes</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.cycleDateNotes || ''}
                        onChange={(e) => handleChange('cycleDateNotes', e.target.value)}
                        placeholder="e.g., Texas architects must renew annually on the calendar year"
                      />
                      <small className="text-muted">Explain the fixed cycle policy to users</small>
                    </div>

                    {formData.fixedCycleStartMonth && formData.fixedCycleStartDay && formData.fixedCycleEndMonth && formData.fixedCycleEndDay && (
                      <div className="alert alert-info">
                        <strong>Preview:</strong> Cycles will run from{' '}
                        {new Date(2024, formData.fixedCycleStartMonth - 1, formData.fixedCycleStartDay).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}{' '}
                        to{' '}
                        {new Date(2024, formData.fixedCycleEndMonth - 1, formData.fixedCycleEndDay).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}{' '}
                        each year
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Reference Links Section */}
              <div className="mb-3">
                <hr />
                <h6 className="mb-3">Reference Links</h6>
                <small className="text-muted d-block mb-3">
                  Add official links to state/profession requirements pages
                </small>

                <div className="mb-3">
                  <label className="form-label">Link 1 Description</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.stateprofessionlink1desc || ''}
                    onChange={(e) => handleChange('stateprofessionlink1desc', e.target.value)}
                    placeholder="e.g., Official NY PE Requirements"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Link 1 URL</label>
                  <input
                    type="url"
                    className="form-control"
                    value={formData.stateprofessionlink1 || ''}
                    onChange={(e) => handleChange('stateprofessionlink1', e.target.value)}
                    placeholder="https://..."
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Link 2 Description</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.stateprofessionlink2desc || ''}
                    onChange={(e) => handleChange('stateprofessionlink2desc', e.target.value)}
                    placeholder="e.g., Continuing Education FAQs"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Link 2 URL</label>
                  <input
                    type="url"
                    className="form-control"
                    value={formData.stateprofessionlink2 || ''}
                    onChange={(e) => handleChange('stateprofessionlink2', e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              </div>

              {/* Category Requirements Section */}
              <div className="mb-3">
                <hr />
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Category Requirements</h6>
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={handleAddCategory}
                  >
                    <i className="bi bi-plus-lg"></i> Add Category
                  </button>
                </div>
                <small className="text-muted d-block mb-3">
                  Define <strong>subject-matter</strong> credit requirements (e.g. HSW, Ethics, Child Abuse).
                  Delivery-method constraints (live vs. self-study hours) belong in the
                  <em> Delivery Method Constraints</em> section above — not here.
                  <br />
                  <strong>Note:</strong> Each category tag must be unique within this state and profession combination.
                </small>

                {categories.length === 0 ? (
                  <div className="alert alert-info">
                    <i className="bi bi-info-circle"></i> No category requirements added yet. 
                    Click "Add Category" to specify requirements for HSW, Ethics, or other categories.
                  </div>
                ) : (
                  <div className="list-group mb-3">
                    {categories.map((category, index) => (
                      <div key={index} className="list-group-item">
                        <div className="row g-2">
                          <div className="col-12">
                            <label className="form-label small">Category Tag <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              className={`form-control form-control-sm mb-2 ${isDuplicateTag(category.categoryTag, index) ? 'is-invalid' : ''}`}
                              value={category.categoryTag}
                              onChange={(e) => handleCategoryChange(index, 'categoryTag', e.target.value.toUpperCase())}
                              placeholder="Enter category name (e.g., HSW, ETHICS)"
                              required
                            />
                            {isDuplicateTag(category.categoryTag, index) && (
                              <div className="invalid-feedback d-block mb-2">
                                This category tag is already used in another category below. Please use a unique name.
                              </div>
                            )}
                            {isDeliveryMethodTag(category.categoryTag) && (
                              <div className="alert alert-warning py-1 px-2 mb-2 small">
                                <i className="bi bi-exclamation-triangle me-1"></i>
                                <strong>{category.categoryTag}</strong> is a delivery-method constraint, not a subject
                                category. Use the <em>Delivery Method Constraints</em> section above instead and
                                remove this entry.
                              </div>
                            )}
                            <div className="d-flex flex-wrap gap-1">
                              <small className="text-muted me-2">Quick select:</small>
                              {availableCategoryTags.map(tag => (
                                <button
                                  key={tag}
                                  type="button"
                                  className={`btn btn-sm ${category.categoryTag === tag ? 'btn-primary' : 'btn-outline-secondary'}`}
                                  onClick={() => handleCategoryChange(index, 'categoryTag', tag)}
                                >
                                  {tag.replace(/_/g, ' ')}
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          <div className="col-md-4">
                            <label className="form-label small">Min Credits <span className="text-danger">*</span></label>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={category.minCredits}
                              onChange={(e) => handleCategoryChange(index, 'minCredits', parseFloat(e.target.value))}
                              min="0"
                              step="0.5"
                              required
                            />
                          </div>
                          
                          <div className="col-md-4">
                            <label className="form-label small">Max Credits</label>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={category.maxCredits || ''}
                              onChange={(e) => handleCategoryChange(index, 'maxCredits', e.target.value ? parseFloat(e.target.value) : undefined)}
                              min="0"
                              step="0.5"
                              placeholder="Optional"
                            />
                          </div>

                          <div className="col-md-4">
                            <label className="form-label small">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger float-end"
                                onClick={() => handleRemoveCategory(index)}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </label>
                          </div>

                          <div className="col-12">
                            <label className="form-label small">Description (Optional)</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={category.description || ''}
                              onChange={(e) => handleCategoryChange(index, 'description', e.target.value)}
                              placeholder="Additional details about this requirement"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={formData.notes || ''}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Additional notes or special requirements..."
                />
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onCancel}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {requirement?._id ? 'Save Changes' : 'Add State Requirement'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StateRequirementForm;
